import { Fragment, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  MoreHorizontal,
  Plus,
  Star,
  Upload,
  Users,
} from 'lucide-react';
import {
  RESPONSE_TO_LEGACY_STATUS,
  RSVP_RESPONSES,
  SystemRole,
  guestDisplayName,
  hasMinimumRole,
  partyAdults,
  type Guest,
  type GuestCapabilities,
  type GuestFormOptions,
  type RsvpResponseValue,
} from '@peles/shared';
import {
  useDeleteEventGuest,
  useEventGuestCapabilities,
  useEventGuestDuplicates,
  useEventGuestOptions,
  useEventGuestSummary,
  useEventGuests,
  useSetEventGuestRsvp,
} from '../../../hooks/useEventGuests';
import { useAuth } from '../../../context/AuthContext';
import { useDialog } from '../../../context/DialogContext';
import { DropdownMenu } from '../../common/DropdownMenu';
import { extractApiErrorMessage } from '../../../utils/apiError';
import { ExportSeatingMenu } from '../ExportSeatingMenu';
import { PrintablesButton } from '../../printables/PrintablesButton';
import { GuestBulkBar } from './GuestBulkBar';
import { GuestFilterBar } from './GuestFilterBar';
import { GuestQuickAdd } from './GuestQuickAdd';
import { GuestStatTiles } from './GuestStatTiles';
import { RsvpMenu } from './RsvpMenu';
import { SeatPicker } from './SeatPicker';
import { GUEST_PAGE_SIZE, guestListQuery, useGuestListParams } from './useGuestListParams';
import type { GuestGroupBy } from './useGuestListParams';
import { useSeatGuests } from './useSeatGuests';
import { GuestFormModal } from '../form/GuestFormModal';
import { GuestDuplicatesModal } from '../GuestDuplicatesModal';
import { GuestImportWizard } from '../GuestImportWizard';
import { PhoneLink } from '../../common/PhoneLink';
import { RecordConversations } from '../../messaging/RecordConversations';
import { useThreadCounts } from '../../../hooks/useThreadCounts';

export interface GuestListViewProps {
  eventId: string;
  /** Shown above the list. */
  title?: string;
}

const countLabel = (adults: number, kids: number) =>
  kids > 0 ? `${adults}+${kids}` : String(adults);

/**
 * "2+1" — adults, then children when there are any. Once the guest has said
 * yes it is who is coming, which can be fewer than the party invited.
 */
export function partyBadge(
  guest: Pick<Guest, 'maleCount' | 'femaleCount' | 'kidCount' | 'attending'>,
): string {
  if (guest.attending) return countLabel(guest.attending.adults, guest.attending.kids);
  return countLabel(partyAdults(guest), guest.kidCount);
}

/** What the badge's head count means, and what was invited when fewer are coming. */
export function partyBadgeTitle(
  guest: Pick<Guest, 'maleCount' | 'femaleCount' | 'kidCount' | 'attending'>,
): string {
  if (!guest.attending) return 'Invited: adults + children';
  const invited = countLabel(partyAdults(guest), guest.kidCount);
  const coming = partyBadge(guest);
  return coming === invited
    ? 'Coming: adults + children'
    : `Coming: adults + children (invited ${invited})`;
}

/** "2× Beef, 1× Fish" from the party's members, else the party's own note. */
export function mealSummary(guest: Guest): string {
  const counts = new Map<string, number>();
  for (const member of guest.partyMembers ?? []) {
    if (member.mealOptionName) {
      counts.set(member.mealOptionName, (counts.get(member.mealOptionName) ?? 0) + 1);
    }
  }
  if (counts.size) return [...counts].map(([meal, n]) => `${n}× ${meal}`).join(', ');
  return guest.mealPreference || '';
}

function labelFor(list: GuestFormOptions['sides'] | undefined, value: string | null) {
  if (!value) return '';
  return list?.find((o) => o.value === value)?.label ?? value;
}

function groupKey(guest: Guest, group: GuestGroupBy, options?: GuestFormOptions): string {
  switch (group) {
    case 'side':
      return labelFor(options?.sides, guest.side) || 'No side';
    case 'familyGroup':
      return guest.familyGroup || 'No family group';
    case 'table':
      return guest.tableNumber != null ? `Table ${guest.tableNumber}` : 'Not seated';
    case 'response':
      return (
        RSVP_RESPONSES.find((option) => option.value === (guest.response ?? 'PENDING'))?.label ??
        'Awaiting reply'
      );
    default:
      return '';
  }
}

const NO_CAPABILITIES: GuestCapabilities = {
  read: true,
  write: false,
  delete: false,
  producerFields: false,
  rsvp: false,
  relations: false,
  seating: false,
  invites: false,
  print: false,
};

/**
 * An event's guest list — one component for the producer's Guests tab and the
 * client portal's. Paging, sorting and every filter happen on the server and
 * live in the address; the numbers over it count the whole event. What the
 * caller may do comes from `GET …/guests/capabilities`, the same answer the
 * server enforces, so a client sees exactly the actions their flags allow.
 * Owns its form, import and duplicate dialogs.
 */
export function GuestListView({ eventId, title = 'Guests' }: GuestListViewProps) {
  const { state, update, clearFilters, hasFilters } = useGuestListParams();
  const query = useMemo(() => guestListQuery(state), [state]);
  const { data: page, isLoading, isFetching, isError, refetch } = useEventGuests(eventId, query);
  const { data: summary } = useEventGuestSummary(eventId);
  const { data: options } = useEventGuestOptions(eventId);
  const { data: caps = NO_CAPABILITIES } = useEventGuestCapabilities(eventId);
  const { data: duplicates = [] } = useEventGuestDuplicates(eventId, caps.producerFields);
  const { user } = useAuth();
  const { confirm, alert } = useDialog();
  const setRsvp = useSetEventGuestRsvp(eventId);
  const deleteGuest = useDeleteEventGuest(eventId);
  const { seat } = useSeatGuests(eventId);

  const [formGuest, setFormGuest] = useState<Guest | 'new' | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [duplicatesOpen, setDuplicatesOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());

  // A selection is of rows on screen: a new page or filter starts it afresh.
  useEffect(() => setSelected(new Set()), [query, eventId]);

  const guests = useMemo(() => page?.data ?? [], [page]);
  const total = page?.total ?? 0;
  /*
   * The conversation icon is the office's (COMMUNICATIONS-DESIGN H3/H5): this
   * list is the client portal's Guests tab too, and the server lets anybody
   * below PLANNER attach a conversation to an event only — a guest icon there
   * would be a button that refuses. Nobody shown one, nothing counted.
   */
  const office = user != null && hasMinimumRole(user.systemRole, SystemRole.PLANNER);
  const { countFor: threadCountFor, refetch: refetchThreadCounts } = useThreadCounts(
    'Guest',
    office ? guests.map((guest) => guest.id) : [],
  );
  const selectable = caps.write || caps.delete || caps.rsvp || caps.seating;
  const allSelected = guests.length > 0 && guests.every((g) => selected.has(g.id));

  const toggle = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(guests.map((guest) => guest.id)));

  const answer = async (guest: Guest, response: RsvpResponseValue) => {
    try {
      await setRsvp.mutateAsync({ guestId: guest.id, input: { response } });
    } catch (err) {
      await alert({
        title: 'Could not change the RSVP',
        message: extractApiErrorMessage(err, 'The answer was not changed.'),
        variant: 'error',
      });
    }
  };

  const remove = async (guest: Guest) => {
    const ok = await confirm({
      title: 'Delete guest',
      message: `Delete ${guestDisplayName(guest)}? This cannot be undone.`,
      confirmLabel: 'Delete',
    });
    if (!ok) return;
    try {
      await deleteGuest.mutateAsync(guest.id);
    } catch (err) {
      await alert({
        title: 'Could not delete',
        message: extractApiErrorMessage(err, 'The guest was not deleted.'),
        variant: 'error',
      });
    }
  };

  const rowActions = (guest: Guest) => {
    const items = [
      ...(caps.write ? [{ id: 'edit', label: 'Edit', onSelect: () => setFormGuest(guest) }] : []),
      ...(caps.delete
        ? [
            {
              id: 'delete',
              label: 'Delete',
              dividerBefore: true,
              onSelect: () => void remove(guest),
            },
          ]
        : []),
    ];
    if (items.length === 0) return null;
    return (
      <DropdownMenu
        align="end"
        ariaLabel={`Actions for ${guestDisplayName(guest)}`}
        menuClassName="w-44"
        items={items}
        trigger={(props) => (
          <button
            type="button"
            {...props}
            aria-label={`Actions for ${guestDisplayName(guest)}`}
            className="p-2 rounded-lg text-gray-500 hover:bg-gray-100 min-h-[40px] min-w-[40px] inline-flex items-center justify-center"
          >
            <MoreHorizontal size={18} aria-hidden />
          </button>
        )}
      />
    );
  };

  const nameCell = (guest: Guest) => (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5 flex-wrap">
        {caps.write ? (
          <button
            type="button"
            onClick={() => setFormGuest(guest)}
            className="font-medium text-gray-900 hover:text-primary-700 text-left"
          >
            {guestDisplayName(guest)}
          </button>
        ) : (
          <span className="font-medium text-gray-900">{guestDisplayName(guest)}</span>
        )}
        {guest.isVip && (
          <Star size={13} className="text-amber-500 fill-amber-400" aria-label="VIP" />
        )}
        {office && (
          <RecordConversations
            entityType="Guest"
            entityId={guest.id}
            variant="icon"
            count={threadCountFor(guest.id)}
            onActivity={() => void refetchThreadCounts()}
          />
        )}
        <span
          className="px-1.5 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-medium"
          title={partyBadgeTitle(guest)}
        >
          {partyBadge(guest)}
        </span>
        {!caps.producerFields && user && guest.createdByUserId === user.id && (
          <span className="px-1.5 py-0.5 rounded-md bg-primary-50 text-primary-700 text-xs">
            Added by you
          </span>
        )}
      </div>
      {(guest.email || guest.phone) && (
        <div className="flex items-center gap-2 text-xs text-gray-500 min-w-0">
          {guest.email && <span className="truncate">{guest.email}</span>}
          {guest.email && guest.phone && <span aria-hidden="true">·</span>}
          <PhoneLink
            phone={guest.phone}
            name={guestDisplayName(guest)}
            className="text-gray-500 hover:text-primary-700"
            whatsappIconClassName="h-3.5 w-3.5"
          />
        </div>
      )}
      {(guest.tags?.length ?? 0) > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          {guest.tags
            ?.filter((t) => t !== 'vip')
            .map((tag) => (
              <span
                key={tag}
                className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600 text-[11px]"
              >
                {labelFor(options?.tags, tag)}
              </span>
            ))}
        </div>
      )}
    </div>
  );

  const tableCell = (guest: Guest) =>
    caps.seating ? (
      <SeatPicker
        eventId={eventId}
        tableNumber={guest.tableNumber}
        partySize={Math.max(1, guest.maleCount + guest.femaleCount + guest.kidCount)}
        label={
          guest.tableNumber != null
            ? `T${guest.tableNumber}${guest.seatNumber ? ` · ${guest.seatNumber}` : ''}`
            : undefined
        }
        onChoose={(tableNumber) => void seat({ guestIds: [guest.id], tableNumber })}
      />
    ) : guest.tableNumber != null ? (
      <span className="text-sm text-gray-700">
        Table {guest.tableNumber}
        {guest.seatNumber ? ` · seat ${guest.seatNumber}` : ''}
      </span>
    ) : (
      <span className="text-sm text-gray-400">—</span>
    );

  const rsvpCell = (guest: Guest) => (
    <RsvpMenu
      response={guest.response}
      guestName={guestDisplayName(guest)}
      onChange={caps.rsvp ? (response) => void answer(guest, response) : undefined}
    />
  );

  const withGroups = (render: (guest: Guest) => ReactNode, header: (key: string) => ReactNode) =>
    guests.map((guest, index) => {
      const key = groupKey(guest, state.group, options);
      const showHeader =
        state.group !== 'none' &&
        (index === 0 || groupKey(guests[index - 1], state.group, options) !== key);
      return (
        <Fragment key={guest.id}>
          {showHeader && header(key)}
          {render(guest)}
        </Fragment>
      );
    });

  const from = total === 0 ? 0 : (state.page - 1) * GUEST_PAGE_SIZE + 1;
  const to = Math.min(total, state.page * GUEST_PAGE_SIZE);
  const lastPage = Math.max(1, Math.ceil(total / GUEST_PAGE_SIZE));

  const exportFilters = {
    side: state.side,
    rsvpStatus: state.response.map((r) => RESPONSE_TO_LEGACY_STATUS[r]),
    familyGroup: state.familyGroup,
  };

  return (
    <div className={`space-y-4 ${selected.size > 0 ? 'pb-28 md:pb-0' : ''}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-500" data-testid="guest-count">
            {total} {total === 1 ? 'guest' : 'guests'}
            {hasFilters ? ' match' : ''}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {caps.producerFields && duplicates.length > 0 && (
            <button
              type="button"
              onClick={() => setDuplicatesOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-800 text-sm"
            >
              <Copy size={15} aria-hidden />
              {duplicates.length} possible duplicate{duplicates.length === 1 ? '' : 's'}
            </button>
          )}
          {caps.print && <ExportSeatingMenu eventId={eventId} filters={exportFilters} />}
          {caps.print && (
            <PrintablesButton eventId={eventId} chooseInvitation={caps.producerFields} />
          )}
          {caps.write && (
            <button
              type="button"
              onClick={() => setImportOpen(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
            >
              <Upload size={15} aria-hidden />
              Import
            </button>
          )}
          {caps.write && (
            <button
              type="button"
              onClick={() => setFormGuest('new')}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm hover:bg-primary-700"
            >
              <Plus size={15} aria-hidden />
              Add guest
            </button>
          )}
        </div>
      </div>

      <GuestStatTiles summary={summary} state={state} onFilter={update} />

      <GuestFilterBar
        state={state}
        options={options}
        onChange={update}
        onClear={clearFilters}
        hasFilters={hasFilters}
        showSource={caps.producerFields}
      />

      {caps.write && <GuestQuickAdd eventId={eventId} />}

      <GuestBulkBar
        eventId={eventId}
        selectedIds={guests.filter((g) => selected.has(g.id)).map((g) => g.id)}
        capabilities={caps}
        options={options}
        onClear={() => setSelected(new Set())}
      />

      {isLoading ? (
        <div className="space-y-2" aria-label="Loading guests" role="status">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="h-14 rounded-xl bg-gray-100 animate-pulse" />
          ))}
        </div>
      ) : isError ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm text-red-800">The guest list could not be loaded.</p>
          <button
            type="button"
            onClick={() => void refetch()}
            className="mt-2 text-sm font-medium text-red-800 underline"
          >
            Try again
          </button>
        </div>
      ) : guests.length === 0 ? (
        <div className="rounded-2xl border border-gray-200 bg-white p-10 text-center">
          <Users className="mx-auto h-10 w-10 text-gray-300" aria-hidden />
          {hasFilters ? (
            <>
              <p className="mt-2 text-gray-600">No guest matches these filters.</p>
              <button
                type="button"
                onClick={clearFilters}
                className="mt-2 text-sm font-medium text-primary-700"
              >
                Clear filters
              </button>
            </>
          ) : (
            <p className="mt-2 text-gray-600">
              No guests yet.
              {caps.write ? ' Add one, type a list into Quick add, or import a file.' : ''}
            </p>
          )}
        </div>
      ) : (
        <div className={isFetching ? 'opacity-70 transition-opacity' : ''}>
          {/* Desktop */}
          <div className="hidden md:block bg-white rounded-2xl border border-gray-200 overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                  {selectable && (
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        aria-label="Select all guests on this page"
                        checked={allSelected}
                        onChange={toggleAll}
                      />
                    </th>
                  )}
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">RSVP</th>
                  <th className="px-4 py-3">Side</th>
                  <th className="px-4 py-3">Family</th>
                  <th className="px-4 py-3">Table</th>
                  <th className="px-4 py-3">Meal</th>
                  <th className="px-4 py-3 w-12">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {withGroups(
                  (guest) => (
                    <tr
                      key={guest.id}
                      data-testid={`guest-row-${guest.id}`}
                      className={selected.has(guest.id) ? 'bg-primary-50' : 'hover:bg-gray-50'}
                    >
                      {selectable && (
                        <td className="px-4 py-3 align-top">
                          <input
                            type="checkbox"
                            aria-label={`Select ${guestDisplayName(guest)}`}
                            checked={selected.has(guest.id)}
                            onChange={() => toggle(guest.id)}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3 align-top max-w-xs">{nameCell(guest)}</td>
                      <td className="px-4 py-3 align-top">{rsvpCell(guest)}</td>
                      <td className="px-4 py-3 align-top text-sm text-gray-700">
                        {labelFor(options?.sides, guest.side) || '—'}
                      </td>
                      <td className="px-4 py-3 align-top text-sm text-gray-700">
                        {guest.familyGroup || '—'}
                      </td>
                      <td className="px-4 py-3 align-top">{tableCell(guest)}</td>
                      <td className="px-4 py-3 align-top text-sm text-gray-700">
                        {mealSummary(guest) || '—'}
                      </td>
                      <td className="px-2 py-2 align-top text-right">{rowActions(guest)}</td>
                    </tr>
                  ),
                  (key) => (
                    <tr key={`group-${key}`} className="bg-gray-50">
                      <th
                        colSpan={selectable ? 8 : 7}
                        scope="colgroup"
                        className="px-4 py-2 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide"
                      >
                        {key}
                      </th>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>

          {/* Phone */}
          <ul className="md:hidden space-y-2">
            {withGroups(
              (guest) => (
                <li
                  key={guest.id}
                  data-testid={`guest-card-${guest.id}`}
                  className={`bg-white rounded-2xl border p-3 ${
                    selected.has(guest.id)
                      ? 'border-primary-300 ring-1 ring-primary-200'
                      : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {selectable && (
                      <input
                        type="checkbox"
                        aria-label={`Select ${guestDisplayName(guest)}`}
                        checked={selected.has(guest.id)}
                        onChange={() => toggle(guest.id)}
                        className="mt-1.5 h-5 w-5"
                      />
                    )}
                    <div className="flex-1 min-w-0">{nameCell(guest)}</div>
                    {rowActions(guest)}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    {rsvpCell(guest)}
                    {tableCell(guest)}
                    {mealSummary(guest) && (
                      <span className="text-xs text-gray-600">{mealSummary(guest)}</span>
                    )}
                  </div>
                </li>
              ),
              (key) => (
                <li
                  key={`group-${key}`}
                  className="pt-2 text-xs font-semibold text-gray-600 uppercase tracking-wide"
                >
                  {key}
                </li>
              ),
            )}
          </ul>

          <nav
            aria-label="Guest pages"
            className="mt-3 flex items-center justify-between text-sm text-gray-600"
          >
            <span>
              {from}–{to} of {total}
            </span>
            <span className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => update({ page: state.page - 1 })}
                disabled={state.page <= 1}
                aria-label="Previous page"
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronLeft size={18} aria-hidden />
              </button>
              <span>
                Page {state.page} of {lastPage}
              </span>
              <button
                type="button"
                onClick={() => update({ page: state.page + 1 })}
                disabled={state.page >= lastPage}
                aria-label="Next page"
                className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-40"
              >
                <ChevronRight size={18} aria-hidden />
              </button>
            </span>
          </nav>
        </div>
      )}

      {formGuest && (
        <GuestFormModal
          open
          eventId={eventId}
          guest={formGuest === 'new' ? null : formGuest}
          capabilities={caps}
          options={options}
          onClose={() => setFormGuest(null)}
        />
      )}
      {importOpen && (
        <GuestImportWizard
          open
          eventId={eventId}
          options={options}
          onClose={() => setImportOpen(false)}
        />
      )}
      {duplicatesOpen && (
        <GuestDuplicatesModal
          open
          eventId={eventId}
          groups={duplicates}
          onClose={() => setDuplicatesOpen(false)}
        />
      )}
    </div>
  );
}
